import { useAuthenticator } from '@aws-amplify/ui-react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

export const RequireAuth = ({ children }: { children?: ReactNode }) => {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { authStatus, signOut } = useAuthenticator((context) => [
    context.authStatus,
    context.signOut,
  ])
  const go = useNavigate()

  const [showLogoutWarning, setShowLogoutWarning] = useState(false)
  const [countdown, setCountdown] = useState(30)
  const [originalTitle, setOriginalTitle] = useState('')
  const [hasActiveNotifications, setHasActiveNotifications] = useState(false)

  // Use refs to avoid stale closures
  const titleFlashIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null)
  const activeNotificationRef = useRef<Notification | null>(null)

  // Initialize broadcast channel once on mount
  useEffect(() => {
    const channel = new BroadcastChannel('auth-session')
    broadcastChannelRef.current = channel

    return () => {
      channel.close()
      broadcastChannelRef.current = null
    }
  }, [])

  const handleLogout = useCallback(async () => {
    // Clear the countdown first
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }

    // Clear all notifications and title flashing
    clearAllBrowserNotifications()
    setShowLogoutWarning(false)

    // Broadcast logout message to other tabs
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({ type: 'LOGOUT' })
    }

    try {
      await signOut() // Actually sign out the user using Amplify
    } catch (error) {
      console.error('Error signing out:', error)
      go('/logout') // Fallback to navigation if signOut fails
    }
  }, [signOut, go])

  const startCountdown = useCallback(() => {
    let timeLeft = 30
    setCountdown(timeLeft)

    const countdownInterval = setInterval(() => {
      timeLeft -= 1
      setCountdown(timeLeft)

      if (timeLeft <= 0) {
        clearInterval(countdownInterval)
        countdownIntervalRef.current = null
        handleLogout()
      }
    }, 1000)

    countdownIntervalRef.current = countdownInterval
  }, [handleLogout])

  const showNotificationAlert = useCallback(async () => {
    setOriginalTitle(document.title)
    setHasActiveNotifications(true)

    if (Notification.permission === 'default') {
      await Notification.requestPermission()
    }

    if (Notification.permission === 'granted') {
      const notification = new Notification('Session Expiring Soon', {
        body: 'You will be logged out in 30 seconds due to inactivity.',
        icon: '/favicon.ico',
        tag: 'session-warning',
        requireInteraction: true,
      })

      activeNotificationRef.current = notification

      notification.onclick = () => {
        window.focus()
        notification.close()
        activeNotificationRef.current = null
      }

      notification.onclose = () => {
        activeNotificationRef.current = null
      }
    }

    playNotificationSound()
    startTitleFlashing()
  }, [])

  // Set up idle timer with proper dependencies
  const { reset, pause, resume } = useIdleTimer({
    timeout: 1000 * 60 * 20, // 20min
    onIdle: () => {
      console.log('User has been idle for 20 minutes, showing logout warning')
      setShowLogoutWarning(true)
      pause()
      showNotificationAlert()
      startCountdown()

      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({ type: 'LOGOUT_WARNING' })
      }
    },
    onAction: () => {
      //user is actively interacting with our app
      if (showLogoutWarning) {
        handleStaySignedIn()
      }
      // Always broadcast activity to other tabs
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({ type: 'USER_ACTIVE' })
      }
    },
  })

  // Handle staying signed in
  const handleStaySignedIn = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }

    clearAllBrowserNotifications()
    setShowLogoutWarning(false)
    reset()
    resume()

    if (broadcastChannelRef.current) {
      // Broadcast to clear notifications on all tabs
      broadcastChannelRef.current.postMessage({ type: 'CLEAR_NOTIFICATIONS' })
      broadcastChannelRef.current.postMessage({ type: 'STAY_SIGNED_IN' })
    }
  }, [reset, resume])

  // Set up broadcast channel message listener
  useEffect(() => {
    const channel = broadcastChannelRef.current
    if (!channel) return

    const handleMessage = (event: MessageEvent) => {
      const { type } = event.data

      switch (type) {
        case 'LOGOUT_WARNING':
          if (!showLogoutWarning) {
            setShowLogoutWarning(true)
            pause()
            showNotificationAlert()
            startCountdown()
          }
          break
        case 'STAY_SIGNED_IN':
          // Always clear notifications on this tab, regardless of state
          clearAllBrowserNotifications()
          if (showLogoutWarning) {
            handleStaySignedIn()
          } else if (hasActiveNotifications) {
            // This tab had notifications but no dialog, still reset timer
            reset()
          }
          break
        case 'CLEAR_NOTIFICATIONS':
          // Force clear notifications on all tabs
          clearAllBrowserNotifications()
          break
        case 'LOGOUT':
          handleLogout()
          break
        case 'RESET_ACTIVITY':
          reset()
          break
      }
    }

    channel.onmessage = handleMessage

    return () => {
      if (channel.onmessage === handleMessage) {
        channel.onmessage = null
      }
    }
  }, [
    showLogoutWarning,
    hasActiveNotifications,
    pause,
    reset,
    handleLogout,
    handleStaySignedIn,
    showNotificationAlert,
    startCountdown,
  ])

  // Broadcast user activity to other tabs
  useEffect(() => {
    const handleUserActivity = () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({ type: 'RESET_ACTIVITY' })
      }
    }

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ]

    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true })
    })

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity)
      })
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllBrowserNotifications()
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
      }
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close()
      }
    }
  }, [])

  const playNotificationSound = () => {
    const audio = new Audio()
    audio.src =
      'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwfCD2F2fPNeSsFJnnJ8N+OPwsRVa7n57BdGQU+ltryy3ojBSl+zfPZiTYIF2m79uSaSwsOUarm7bllHgg2jdXzzn0vBSF1xe/eizEIHWq+8+OZUQ0LTKXh8bllHgg2jdT0z3wvBSJ2xu/eizEIHGq+8+OZUQ0LTKXh8bllHgg2jdTy0H4wBiFzxe7fjTMIGmq+9OOaUAwKTaTg87NmGQU7k9n0yXkpBSh+zvPZijgIF2m79uSaSwsOUarm7bllHgg2jdXzz3wvBSF1xe/eizEIHWq+8+OZUQ0LTKXh8rpmHwg8hdn0zXkqBSh+zvLZizgIF2m79uSaSwsOUarm7bllHgg2jdXzz3wvBSF1xe/eizEIHWq+8+OZUQ0LTKXh8rpmHwg8hdn0zXkqBSh+zvLZizgIF2m79uSaSwsOUarm7bllHgg2jdXzz3wvBSF1xe/eizEIHWq+8+OZUQ0LTKXh8rpmHwg8hdn0zXkqBSh+zvLZizgIF2m79uSaSwsOUarm7bllHgg2jdXzz3wvBSF1xe/eizEIHWq+8+OZUQ0LTKXh8rpmHwg8hdn0zXkqBSh+zvLZizgIF2m79uSaSwsOUarm7bllHgg2jdXzz3wvBSF1xe/eizEIHWq+8+OZUQ0LTKXh8rpmHwg8hdn0zXkqBSh+zvLZizgIF2m79uSaSwsOUarm7bllHgg2jdXzz3wvBSF1xe/eizEIHWq+8+OZUQ0LTKXh8rpmHwg8hdn0zXkqBSh+zvLZizgIF2m79uSaSwsOUa=='

    audio.play().catch((e) => {
      console.log('Custom notification sound failed, using system default')
    })
  }

  const startTitleFlashing = () => {
    let isOriginal = true
    const interval = setInterval(() => {
      document.title = isOriginal
        ? '🚨 SESSION EXPIRING SOON!'
        : originalTitle || 'Session Warning'
      isOriginal = !isOriginal
    }, 1000)

    titleFlashIntervalRef.current = interval
  }

  const stopTitleFlashing = () => {
    if (titleFlashIntervalRef.current) {
      clearInterval(titleFlashIntervalRef.current)
      titleFlashIntervalRef.current = null
    }
    // Restore original title
    if (originalTitle) {
      document.title = originalTitle
    } else {
      // If originalTitle wasn't captured, use a reasonable default
      document.title = 'North-Star Care Provider Portal'
    }
  }

  const clearAllBrowserNotifications = () => {
    if (activeNotificationRef.current) {
      activeNotificationRef.current.close()
      activeNotificationRef.current = null
    }
    stopTitleFlashing()
    setHasActiveNotifications(false)
  }

  if (authStatus !== 'authenticated') {
    return (
      <Navigate
        to='/'
        state={{
          from: location,
          searchParams: Object.fromEntries(searchParams),
        }}
        replace
      />
    )
  }

  return (
    <>
      {children}

      <Dialog
        open={showLogoutWarning}
        onClose={() => {}} // Prevent closing by clicking outside
        maxWidth='sm'
        fullWidth
        disableEscapeKeyDown // Prevent closing with Escape key
      >
        <DialogTitle>Session About to Expire</DialogTitle>
        <DialogContent>
          <Typography variant='body1' gutterBottom>
            You have been inactive for a while. For your security, you will be
            automatically logged out in <strong>{countdown} seconds</strong>.
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Would you like to stay signed in or log out now?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogout} color='secondary' variant='outlined'>
            Log Out Now
          </Button>
          <Button
            onClick={handleStaySignedIn}
            color='primary'
            variant='contained'
          >
            Stay Signed In
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
